/**
 * Error reporting, and the postbox for problem reports, through Sentry.
 *
 * Everything Sentry is set up here and nowhere else, so main.tsx calls one
 * function and the problem-report sheet another, and a build without a DSN quietly
 * does nothing. Its settings are build variables (VITE_SENTRY_*), not
 * tunables: they differ by deployment, not by decision.
 *
 * Who uses it: main.tsx (initSentry, SentryErrorBoundary), AuthContext
 * (setSentryUser, the id only), the Header's account menu and Today
 * (canReportProblem, the guard on the two doors) and ReportProblemSheet
 * (sendProblemReport). The form itself is Grow's own sheet, not Sentry's
 * widget. console.error anywhere in the app is sent too
 * (captureConsoleIntegration), which is why expected failures log with
 * console.warn.
 *
 * Local runs send nothing (board D75): Sentry starts only in a production
 * build with a DSN, or when VITE_SENTRY_LOCAL=1 is set for a local walk
 * (environment "local"). A real staging site builds with
 * VITE_SENTRY_ENVIRONMENT=staging. Known noise is filtered (KNOWN_NOISE in
 * sentryRules, each with its reason). Nothing the gardener sees changes.
 *
 * @module sentry
 */

import * as Sentry from '@sentry/react';
import { KNOWN_NOISE, sentryBuildPlan } from './sentryRules';
import { withReferenceTag } from './serverFailureCore';
import type { ReportAttachment } from './reportProblem';

/**
 * Error reporting for the browser half of Grow.
 *
 * The backend has reported to Sentry for a while; the page did not, so a crash
 * on somebody's phone was a crash nobody heard about. Ten friends testing an
 * app do not file bug reports — they stop opening it. This is how a broken
 * screen in a garden in Crossville turns up on a desk, with a stack trace,
 * without anyone having to say anything.
 *
 * Two front ends report to the same Sentry organisation, this one and Garden
 * Gameplan's. They belong in separate projects, which is what the DSN decides;
 * the two things set here on top of that — a `release` of `grow@<commit>`
 * and an `app` tag — mean that even an event that lands in the wrong project
 * says whose it is, and which build.
 *
 * The env names match Garden Gameplan's client so that one set of deployment
 * notes covers both. Without VITE_SENTRY_DSN nothing here does anything.
 *
 * @returns Whether Sentry was started.
 */
export function initSentry(): boolean {
  // Each variable named, not the whole of import.meta.env: naming the object
  // writes every VITE_ variable the build had into the bundle (env-vars).
  const env = {
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    VITE_SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    VITE_SENTRY_TRACES_SAMPLE_RATE: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    VITE_SENTRY_DEBUG: import.meta.env.VITE_SENTRY_DEBUG,
    VITE_SENTRY_LOCAL: import.meta.env.VITE_SENTRY_LOCAL,
    PROD: import.meta.env.PROD,
  };
  const dsn = (env.VITE_SENTRY_DSN ?? '').trim();
  const plan = sentryBuildPlan({
    dsn,
    isProductionBuild: Boolean(env.PROD),
    localOptIn: env.VITE_SENTRY_LOCAL,
    environment: env.VITE_SENTRY_ENVIRONMENT,
  });
  if (!plan.enabled) return false;

  const { environment } = plan;
  const rawRate = (env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '').trim();
  const parsedRate = rawRate ? Number.parseFloat(rawRate) : 0;

  Sentry.init({
    dsn,
    environment,
    release: __GROW_RELEASE__,
    debug: (env.VITE_SENTRY_DEBUG ?? '').trim().toLowerCase() === 'true',
    // A gardener's email has no business in an error report. The user id is
    // attached separately, by setSentryUser, and that is all.
    sendDefaultPii: false,
    // Known noise, not faults (KNOWN_NOISE, each with its reason).
    ignoreErrors: KNOWN_NOISE,
    // Every event is findable by `reference:<first 8 of its id>`, the code the gardener's message shows (E3).
    beforeSend: (event) => withReferenceTag(event),
    integrations: [
      Sentry.browserTracingIntegration(),
      // The app already says console.error at the places it knows are wrong
      // (a failed sign-in, a lookup that came back empty). Those are exactly
      // the moments worth hearing about, so they go too.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    // Errors are the point. Performance traces are off unless asked for,
    // so the quota is spent on the thing that matters.
    tracesSampleRate: Number.isFinite(parsedRate) ? parsedRate : 0,
    initialScope: { tags: { app: 'grow' } },
  });

  return true;
}

/**
 * Who was signed in when something broke. The id only.
 *
 * @param userId - The Grow user id, or null when nobody is signed in.
 */
export function setSentryUser(userId: number | null): void {
  Sentry.setUser(userId === null ? null : { id: String(userId) });
}

/** Re-exported so main.tsx has one import for reporting. */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

/**
 * Whether there is anywhere for a problem report to go. False without a DSN.
 *
 * Guards both doors to the report sheet (the account menu's Report a problem
 * and Today's "Let us know"): a door that opens nothing is worse than none.
 *
 * @returns True once Sentry's client is running.
 */
export function canReportProblem(): boolean {
  return Boolean(Sentry.getClient());
}

/**
 * Hands one problem report to Sentry, which is only the postbox. The user id is
 * already on the scope (setSentryUser); no name or e-mail is collected.
 *
 * @param params.message - What the gardener wrote.
 * @param params.tags - Filterable tags (source, language, theme, page).
 * @param attachments - The gardener's one picture, or none.
 * @returns Resolves with the event id once Sentry answered; rejects when it did not (also after 5 seconds of silence).
 */
export function sendProblemReport(
  params: { message: string; tags: Record<string, string> },
  attachments: ReportAttachment[],
): Promise<string> {
  return Sentry.sendFeedback(
    { message: params.message, source: params.tags.source, tags: params.tags },
    { attachments: attachments.length ? attachments : undefined, includeReplay: false },
  );
}
